import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Route guard.
 * - allowedRoles  : enforces role membership (e.g. ["Admin", "Staff"]).
 * - module        : optional permission key (e.g. "seasons"). If provided,
 *                   the user must hold at least `requiredLevel` on that module.
 * - requiredLevel : "none" | "view" | "edit" | "delete" — defaults to "view".
 *
 * Admin always passes the module check (Admin's effective permissions are
 * `delete` on every module via get_effective_permissions).
 */
export const ProtectedRoute = ({ children, allowedRoles = [], module: moduleKey = null, requiredLevel = 'view' }) => {
  const { user, loading, hasModuleAccess } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const denied = (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">Access Denied</h1>
        <p className="text-gray-600 dark:text-gray-500">You don't have permission to access this page.</p>
      </div>
    </div>
  );

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return denied;
  }

  if (moduleKey && !hasModuleAccess(moduleKey, requiredLevel)) {
    return denied;
  }

  return children;
};
